import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function PricingPage() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await fetch('https://parrotmate.com/api/billing/plans');
      
      if (response.ok) {
        const data = await response.json();
        setPlans(data);
      } else {
        setError('Failed to load pricing plans');
      }
    } catch (error) {
      console.error('Error fetching plans:', error);
      setError('Failed to load pricing plans');
    } finally {
      setLoading(false);
    }
  };

  const formatPlanForDisplay = (plan) => {
    const estimatedCalls = Math.floor(plan.minutes_included / 2.5);
    
    return {
      id: plan.id,
      name: plan.display_name || plan.name,
      price: plan.is_enterprise ? 'Contact Us' : `$${(plan.monthly_fee / 100).toFixed(2)}`,
      firstMonth: plan.is_enterprise ? 'Custom pricing' :
        plan.first_month_fee ? `$${(plan.first_month_fee / 100).toFixed(2)}` : null,
      badge: plan.badge || (plan.is_popular ? 'Most Popular' : ''),
      popular: Boolean(plan.is_popular),
      enterprise: Boolean(plan.is_enterprise),
      features: [
        `${plan.minutes_included} minutes included (≈ ${estimatedCalls} calls)`,
        `${plan.phone_numbers_included} phone number${plan.phone_numbers_included > 1 ? 's' : ''} included`,
        `Extra phone #: $${(plan.extra_phone_rate / 100).toFixed(2)}/month`,
        `Voice overage: $${(plan.voice_overage_rate / 100).toFixed(2)}/minute`,
        `${plan.retention_days} days recording storage`,
      ],
    };
  };

  if (loading) {
    return (
      <div className="bg-gray-900 py-24 sm:py-32 text-white min-h-screen dark:text-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
            <p className="text-gray-300 dark:text-gray-600">Loading pricing plans...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-900 py-24 sm:py-32 text-white min-h-screen dark:text-white">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="text-center">
            <p className="text-red-400 mb-4">{error}</p>
            <button
              onClick={fetchPlans}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 dark:text-white"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Sort plans by sort_order (Enterprise should have sort_order 4 to appear last)
  const sortedPlans = [...plans].sort((a, b) => {
    return (a.sort_order || 0) - (b.sort_order || 0);
  });
  
  const displayPlans = sortedPlans.map(formatPlanForDisplay);
  return (
    <div className="bg-gray-900 py-24 sm:py-32 text-white min-h-screen dark:text-white">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-3xl text-center mb-16">
          <h2 className="text-4xl font-bold tracking-tight sm:text-5xl mb-6">
            Simple, transparent pricing
          </h2>
          <p className="text-xl leading-8 text-gray-300 mb-4 dark:text-gray-600">
            Choose the plan that's right for your business.
          </p>
          <p className="text-lg text-gray-400">
            All plans include AI voice calls, real-time summaries, and CRM integration.
            Built around a 2½-minute average call.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
          {displayPlans.map((plan) => (
            <div
              key={plan.id}
              className={`relative bg-white text-gray-900 rounded-2xl shadow-xl p-8 ${
                plan.popular ? 'ring-2 ring-teal-400 scale-105' : ''
              } ${plan.enterprise ? 'bg-gradient-to-br from-gray-800 to-gray-900 text-white' : ''}`}
            >
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-lime-400 text-gray-900 px-4 py-2 rounded-full text-sm font-bold dark:text-white">
                    {plan.badge}
                  </span>
                </div>
              )}
              
              <div className="text-center mb-6">
                <h3 className={`text-2xl font-bold mb-3 ${plan.enterprise ? 'text-white' : 'text-gray-900'}`}>
                  {plan.name}
                </h3>
                {!plan.popular && plan.badge && plan.badge !== 0 && plan.badge !== '0' && (
                  <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                    plan.enterprise
                      ? 'bg-gray-700 text-lime-400'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {plan.badge}
                  </span>
                )}
              </div>

              <div className="text-center mb-6">
                <div className={`text-4xl font-bold mb-2 ${
                  plan.enterprise ? 'text-white' : 'text-teal-600'
                }`}>
                  {plan.price}
                  {plan.price !== 'Contact Us' && (
                    <span className={`text-lg font-normal ${
                      plan.enterprise ? 'text-gray-300' : 'text-gray-500'
                    }`}>
                      /month
                    </span>
                  )}
                </div>
                {plan.firstMonth && (
                  <div className={`text-sm font-semibold ${
                    plan.enterprise ? 'text-lime-400' : 'text-teal-600'
                  }`}>
                    First Month {plan.firstMonth}
                  </div>
                )}
              </div>

              <ul className="space-y-4 mb-8">
                {plan.features.map((feature, index) => (
                  <li key={index} className="flex items-start">
                    <svg className={`w-5 h-5 mt-0.5 mr-3 flex-shrink-0 ${
                      plan.enterprise ? 'text-lime-400' : 'text-teal-500'
                    }`} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className={`text-sm ${
                      plan.enterprise ? 'text-gray-300' : 'text-gray-600'
                    }`}>
                      {feature}
                    </span>
                  </li>
                ))}
              </ul>

              {plan.enterprise ? (
                <a
                  href="mailto:sales@parrotmate.com"
                  className="block w-full py-3 px-6 text-center bg-lime-400 text-gray-900 rounded-lg font-semibold hover:bg-lime-500 transition-colors duration-200 dark:text-white"
                >
                  Contact Sales
                </a>
              ) : (
                <Link
                  to="/plans"
                  className={`block w-full py-3 px-6 text-center rounded-lg font-semibold transition-colors duration-200 ${
                    plan.popular
                      ? 'bg-lime-400 text-gray-900 hover:bg-lime-500'
                      : 'bg-teal-600 text-white hover:bg-teal-700'
                  }`}
                >
                  Get Started
                </Link>
              )}
            </div>
          ))}
        </div>

        <div className="text-center">
          <p className="text-gray-400 mb-4">
            All prices in USD. No setup fees. Cancel anytime.
          </p>
          <p className="text-gray-300 dark:text-gray-600">
            Need help choosing? <a href="mailto:support@parrotmate.com" className="text-teal-400 hover:text-teal-300">Contact our team</a>
          </p>
        </div>
      </div>
    </div>
  );
}
